import { supabase } from '../lib/supabase'
import {
  formatFileSize as formatBytes,
  guessMimeType,
  validateAttachment as validateAttachmentFile,
} from '../utils/attachmentLimits'
import { uploadAttachmentFile } from '../utils/attachmentUpload'

export type AttachmentType = 'course_material' | 'worksheet'

export interface VideoAttachment {
  id: string
  video_id: string
  attachment_type: AttachmentType
  file_name: string
  file_path: string
  file_size: number
  mime_type: string
  uploaded_by: string | null
  created_at: string
  updated_at: string
}

export interface VideoAttachmentInsert {
  video_id: string
  attachment_type: AttachmentType
  file_name: string
  file_path: string
  file_size: number
  mime_type: string
  uploaded_by?: string | null
}

export class AttachmentService {
  private static readonly BUCKET_NAME = 'video-attachments'

  /**
   * Get all attachments for a specific video
   */
  static async getVideoAttachments(videoId: string): Promise<VideoAttachment[]> {
    const { data, error } = await supabase
      .from('video_attachments')
      .select('*')
      .eq('video_id', videoId)
      .order('attachment_type', { ascending: true })
      .order('created_at', { ascending: true })

    if (error) {
      console.error('Error fetching video attachments:', error)
      throw new Error(`Failed to fetch video attachments: ${error.message}`)
    }

    return data || []
  }

  // Batch query to get attachments for multiple videos at once
  static async getVideoAttachmentsBatch(videoIds: string[]): Promise<Record<string, VideoAttachment[]>> {
    if (videoIds.length === 0) return {}

    const { data, error } = await supabase
      .from('video_attachments')
      .select('*')
      .in('video_id', videoIds)
      .order('attachment_type', { ascending: true })
      .order('created_at', { ascending: true })

    if (error) {
      console.error('Error fetching video attachments batch:', error)
      throw new Error(`Failed to fetch video attachments batch: ${error.message}`)
    }

    // Group attachments by video_id
    const result: Record<string, VideoAttachment[]> = {}
    videoIds.forEach(id => {
      result[id] = []
    })

    if (data) {
      for (const attachment of data) {
        if (attachment.video_id) {
          if (!result[attachment.video_id]) {
            result[attachment.video_id] = []
          }
          result[attachment.video_id].push(attachment as VideoAttachment)
        }
      }
    }

    return result
  }

  /**
   * Get attachments grouped by type for a video
   */
  static async getVideoAttachmentsByType(videoId: string): Promise<{
    course_materials: VideoAttachment[]
    worksheets: VideoAttachment[]
  }> {
    const attachments = await this.getVideoAttachments(videoId)

    return {
      course_materials: attachments.filter(a => a.attachment_type === 'course_material'),
      worksheets: attachments.filter(a => a.attachment_type === 'worksheet')
    }
  }

  /**
   * Upload a file attachment for a video
   */
  static async uploadAttachment(
    videoId: string,
    file: File,
    type: AttachmentType,
    userId?: string
  ): Promise<VideoAttachment> {
    try {
      // Reject over-sized/unsupported files here: the bucket answers them with a 413/415
      // that only shows up as an opaque console error.
      const validationError = validateAttachmentFile(file)
      if (validationError) {
        throw new Error(validationError)
      }

      // Generate unique file path
      const fileExt = file.name.split('.').pop()
      const fileName = `${videoId}/${type}/${Date.now()}-${Math.random().toString(36).substring(2)}.${fileExt}`

      // Upload file to Supabase Storage, sending the bytes straight there when the file
      // is too big for the /api proxy.
      const uploadData = await uploadAttachmentFile({
        bucket: this.BUCKET_NAME,
        path: fileName,
        file
      })

      // Save attachment record to database
      const attachmentData: VideoAttachmentInsert = {
        video_id: videoId,
        attachment_type: type,
        file_name: file.name,
        file_path: uploadData.path,
        file_size: file.size,
        mime_type: guessMimeType(file) || file.type,
        uploaded_by: userId || null
      }

      const { data: attachment, error: dbError } = await supabase
        .from('video_attachments')
        .insert(attachmentData)
        .select()
        .single()

      if (dbError) {
        // Clean up uploaded file if database insert fails
        await supabase.storage.from(this.BUCKET_NAME).remove([uploadData.path])
        throw new Error(`Database insert failed: ${dbError.message}`)
      }

      return attachment
    } catch (error) {
      console.error('Error uploading attachment:', error)
      throw error
    }
  }

  /**
   * Generate a public download URL for an attachment
   */
  static async getDownloadUrl(attachmentId: string): Promise<string> {
    try {
      // Get attachment info
      const { data: attachment, error: fetchError } = await supabase
        .from('video_attachments')
        .select('file_path, file_name')
        .eq('id', attachmentId)
        .single()

      if (fetchError || !attachment) {
        throw new Error('Attachment not found')
      }

      // Generate public URL with download parameter and custom filename
      const { data } = supabase.storage
        .from(this.BUCKET_NAME)
        .getPublicUrl(attachment.file_path, {
          download: attachment.file_name
        })

      return data.publicUrl
    } catch (error) {
      console.error('Error generating download URL:', error)
      throw error
    }
  }


  /**
   * Delete an attachment (admin/teacher only)
   */
  static async deleteAttachment(attachmentId: string): Promise<void> {
    try {
      // Get attachment info
      const { data: attachment, error: fetchError } = await supabase
        .from('video_attachments')
        .select('file_path')
        .eq('id', attachmentId)
        .single()

      if (fetchError || !attachment) {
        throw new Error('Attachment not found')
      }

      // Delete from database first
      const { error: dbError } = await supabase
        .from('video_attachments')
        .delete()
        .eq('id', attachmentId)

      if (dbError) {
        throw new Error(`Failed to delete attachment record: ${dbError.message}`)
      }

      // Delete file from storage
      const { error: storageError } = await supabase.storage
        .from(this.BUCKET_NAME)
        .remove([attachment.file_path])

      if (storageError) {
        console.warn('Failed to delete file from storage:', storageError)
        // Don't throw here as the database record is already deleted
      }
    } catch (error) {
      console.error('Error deleting attachment:', error)
      throw error
    }
  }

  /**
   * Get file size in human readable format
   */
  static formatFileSize(bytes: number): string {
    return formatBytes(bytes)
  }

  /**
   * Get file type icon based on MIME type
   */
  static getFileIcon(mimeType: string): string {
    if (mimeType.includes('pdf')) return '📄'
    if (mimeType.includes('word') || mimeType.includes('document')) return '📝'
    if (mimeType.includes('excel') || mimeType.includes('spreadsheet')) return '📊'
    if (mimeType.includes('powerpoint') || mimeType.includes('presentation')) return '📋'
    if (mimeType.includes('image')) return '🖼️'
    return '📎'
  }
}